import type { VoiceCredentials, VoiceClient } from './VoiceClient';

/** VoiceControls 依赖的最小结构：真实 VoiceClient 与下面的懒加载壳都满足。 */
export interface VoiceController {
    micEnabled: boolean;
    /** 房间级禁言（主持人）：true 时麦克风按钮锁定为闭麦态。 */
    adminMuted: boolean;
    onHint: (message: string) => void;
    onMicState: (enabled: boolean) => void;
    join(creds: VoiceCredentials): Promise<void>;
    toggleMic(): Promise<boolean>;
    /** 打开麦克风（主持人远程开麦 player:mic-open 复用；不影响已开麦状态）。 */
    openMic(): Promise<boolean>;
    /** 关闭麦克风（主持人远程关麦 player:mic-close 复用；已关麦时幂等）。 */
    closeMic(): Promise<boolean>;
    setAdminMuted(muted: boolean): Promise<void>;
    leave(): Promise<void>;
    dispose(): void;
}

/**
 * SDK 下载总闸：agora SDK（~1.6MB）的下载必须排在 collision.glb（行走
 * 模式硬依赖）之后——两平台一致。设置后任何路径（进房 join / 用户点
 * 麦克风 / 主持人远程开麦）触发 ready() 都先等闸放行；SDK 已加载
 * （impl 命中）则不再等待。main.ts 用首个场景的碰撞门放行（自带 15s
 * 超时兜底）。只认第一次设置（后设不覆盖、不收紧）。
 */
let sdkDownloadGate: Promise<void> = Promise.resolve();
let sdkGateSet = false;
export function setVoiceSdkGate(gate: Promise<void>): void {
    if (sdkGateSet) return;
    sdkGateSet = true;
    sdkDownloadGate = gate.catch(() => { /* 闸异常不得锁死语音 */ });
}

/**
 * VoiceClient 懒加载壳：agora SDK（约 1.6MB / gzip 440KB）不打进主包，
 * 首次真正要进语音频道时才用 import() 下载（时机由 main.ts 控制：首个
 * 场景的碰撞 GLB 落地之后，避开与 collision.glb / 阻塞 LOD 块抢带宽）。
 * 浏览器缓存后再次进房瞬时可用；solo 世界场景永远不会下载。下载失败
 * 静默降级（语音按钮不出现），并允许下次重试。
 */
export class LazyVoice implements VoiceController {
    micEnabled = false;
    /** 禁言标志在 SDK 未加载时也要先记住：ready() 后同步给真实客户端。 */
    adminMuted = false;
    onHint: (message: string) => void = () => {};
    onMicState: (enabled: boolean) => void = () => {};
    private impl?: VoiceClient;
    private loading?: Promise<VoiceClient | undefined>;
    /** SDK 未加载期间 SpatialMixer 已算出的远端音量：ready() 后一次性补套，
     *  否则「音量未变就不再下发」的幂等逻辑会让这些成员一直停在原声。 */
    private pendingVolumes = new Map<number, number>();

    private ready(): Promise<VoiceClient | undefined> {
        if (this.impl) return Promise.resolve(this.impl);
        // 先过 SDK 下载总闸（碰撞 GLB 之后，见 setVoiceSdkGate），再触发
        // import()——闸未放行前任何调用方都只挂起、不产生网络请求。
        return this.loading ??= sdkDownloadGate.then(() => import('./VoiceClient')).then(
            ({ VoiceClient }) => {
                const client = new VoiceClient();
                client.onHint = (msg) => this.onHint(msg);
                client.onMicState = (enabled) => { this.micEnabled = enabled; this.onMicState(enabled); };
                // SDK 此刻才加载（用户此前从未开麦）：把壳上缓存的禁言状态带进
                // 新客户端；复用已有客户端时不在此同步——换房 leave 已按房间重置。
                if (this.adminMuted) void client.setAdminMuted(true);
                for (const [uid, volume] of this.pendingVolumes) client.setRemoteVolume(uid, volume);
                this.pendingVolumes.clear();
                this.impl = client;
                return client;
            },
            (err) => {
                console.warn('[voice] SDK 加载失败，本次会话语音停用', err);
                this.loading = undefined; // 瞬时网络问题不至于永久关死
                return undefined;
            },
        );
    }

    async join(creds: VoiceCredentials): Promise<void> {
        const client = await this.ready();
        if (client) await client.join(creds);
    }

    async toggleMic(): Promise<boolean> {
        const client = await this.ready();
        return client ? client.toggleMic() : false;
    }

    async openMic(): Promise<boolean> {
        const client = await this.ready();
        return client ? client.openMic() : false;
    }

    async closeMic(): Promise<boolean> {
        const client = await this.ready();
        return client ? client.closeMic() : false;
    }

    /** 远端成员按距离衰减的播放音量（0–100）：转发给真实客户端；SDK 未加载
     *  时记账（此时尚无音轨，ready() 后统一补套）。 */
    setRemoteVolume(uid: number, volume: number): void {
        if (this.impl) this.impl.setRemoteVolume(uid, volume);
        else this.pendingVolumes.set(uid, volume);
    }

    setAdminMuted(muted: boolean): Promise<void> {
        this.adminMuted = muted;
        return this.impl ? this.impl.setAdminMuted(muted) : Promise.resolve();
    }

    async leave(): Promise<void> {
        // 禁言随房间：与实现侧 leave() 同时重置壳标志，换房后以新房间快照为准。
        this.adminMuted = false;
        this.pendingVolumes.clear();
        await this.impl?.leave();
    }

    dispose(): void {
        this.impl?.dispose();
    }
}
